const BASE = '/vendor/api';

async function vendorRequest<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (res.status === 204) return undefined as T;

  const data = await res.json().catch(() => ({ error: { message: res.statusText } }));

  if (!res.ok) {
    if (res.status === 401 && !path.includes('/login') && !path.includes('/totp')) {
      window.location.href = '/vendor/login';
    }
    const message = data?.error?.message ?? data?.error ?? 'Unknown error';
    throw new Error(typeof message === 'string' ? message : 'Unknown error');
  }

  return data as T;
}

// ── Types ────────────────────────────────────────────────────────────────────

export interface TenantLicenseState {
  status:    'allowed' | 'blocked';
  graceDays?: number;
}

export type TenantTier = 'standard' | 'pro';
/** On-prem license tiers. Mirrors backend lib/licenseKey.ts. */
export type OnPremTier = 'starter' | 'growth' | 'enterprise';

export interface VendorTenant {
  id:                 string;
  name:               string;
  plan:               'demo' | 'onprem' | 'cloud' | 'expired';
  tier:               TenantTier;
  demo_ends_at:       string | null;
  license_expires_at: string | null;
  stripe_sub_id:      string | null;
  billing_email:      string | null;
  admin_email:        string | null;
  revoked_at:         string | null;
  user_count:         number;
  license_state:      TenantLicenseState;
}

export interface VendorStats {
  total:         number;
  active:        number;
  expiring_soon: number;
  expired:       number;
}

export interface TenantsResponse {
  stats:   VendorStats;
  tenants: VendorTenant[];
}

// ── API ──────────────────────────────────────────────────────────────────────

export const vendorAuthApi = {
  login:      (email: string, password: string) =>
    vendorRequest<{ next: string }>('POST', '/login', { email, password }),
  verifyTotp: (token: string) =>
    vendorRequest<{ ok: boolean }>('POST', '/totp/verify', { token }),
  logout:     () =>
    vendorRequest<{ ok: boolean }>('POST', '/logout'),
};

export const vendorTenantsApi = {
  list: () =>
    vendorRequest<TenantsResponse>('GET', '/tenants'),
  generateKey: (id: string, billing_email: string | undefined, tier: OnPremTier = 'enterprise') =>
    vendorRequest<{ key: string; expires_at: string; tier: OnPremTier; max_users: number | null }>(
      'POST',
      `/tenants/${id}/generate-key`,
      { billing_email, tier },
    ),
  extend: (id: string, expires_at: string) =>
    vendorRequest<{ ok: boolean; expires_at: string }>('PATCH', `/tenants/${id}/extend`, { expires_at }),
  resetDemo: (id: string) =>
    vendorRequest<{ ok: boolean; demo_ends_at: string }>('PATCH', `/tenants/${id}/reset-demo`),
  revoke: (id: string, confirm_name: string) =>
    vendorRequest<{ ok: boolean }>('PATCH', `/tenants/${id}/revoke`, { confirm_name }),
  setTier: (id: string, tier: TenantTier) =>
    vendorRequest<{ id: string; tier: TenantTier }>('PATCH', `/tenants/${id}/tier`, { tier }),
  // Hard delete: irreversible, only allowed on revoked tenants. Wipes all
  // child rows + attachment files from storage. Requires the admin to type
  // the company name as confirmation, same UX as Revoke.
  hardDelete: (id: string, confirm_name: string) =>
    vendorRequest<{ ok: boolean; attachments_deleted: number; blobs_failed: number }>('DELETE', `/tenants/${id}`, { confirm_name }),
};
